// İSG Portal — Araç kaydı (GL #25) saf yardımcıları. api-contract §23.0.2 (plaka), §23.0.4/§23.0.7
// (kilitler). SP ve EP AYNI kuralı okur; sunucu yine de karar verir (`409` yedektir).

import type { TFunction } from 'i18next';
import { isApiError } from '../api/errors';
import type { VehicleDocumentDto, VehicleWriteRequest } from '../api/permits';
import type { ContractorVehicleDto } from '../api/vehicles';
import { resolveApiErrorMessage } from '../i18n/apiError';

/** Görünen plaka üst sınırı (§23.0.2). */
export const VEHICLE_PLATE_MAX = 50;
/** Eşleşme anahtarı (yalnız harf+rakam) üst sınırı (§23.0.2). */
export const VEHICLE_PLATE_KEY_MAX = 20;
export const VEHICLE_DESCRIPTION_MAX = 500;
export const VEHICLE_NOTE_MAX = 500;
/** Belge red gerekçesi üst sınırı — kişi ve araç belgeleri için ortak (sunucu karar verir). */
export const DOCUMENT_REJECT_REASON_MAX = 500;

/** `contractor` = taşeron Yetkili (web-supplier); `host` = ana firma / İSG uzmanı (web-expert). */
export type VehicleActor = 'contractor' | 'host';

/** Görünen plaka (§23.0.2): GİRİLDİĞİ GİBİ — yalnız baştaki/sondaki boşluk kırpılır; büyük/küçük harf ve ayraçlar korunur. */
export function displayPlate(input: string): string {
  return input.trim();
}

/**
 * Eşleşme anahtarı (§23.0.2): YALNIZ Unicode harf ve rakamlar (kategoriler L ve Nd) kalır, kalan her karakter atılır,
 * sonra kültürden bağımsız büyük harf (`toUpperCase()`; ASLA `toLocaleUpperCase('tr')`).
 * `"34.ABC 12"`, `"34-abc-12"`, `"34ABC12"` → `34ABC12`. Yalnız gösterim ipucudur ("zaten kayıtlı");
 * kararı sunucu verir. Boş anahtar = geçersiz plaka (`REQUIRED`).
 */
export function plateKey(input: string): string {
  return input.replace(/[^\p{L}\p{Nd}]/gu, '').toUpperCase();
}

/** Plaka anahtarı değişebilir mi (§23.0.2/§23.0.7). İzin ekranındaki araç için `hasBeenLinked: true` ver. */
export function canEditPlate(
  vehicle: Pick<ContractorVehicleDto, 'hasBeenLinked' | 'isPlateLocked'>,
  actor: VehicleActor,
): boolean {
  return actor === 'host'
    ? !vehicle.isPlateLocked
    : !vehicle.hasBeenLinked && !vehicle.isPlateLocked;
}

/** Taşeronun araç silme engelleri (ana firmanın "kullanımda" kilidi ona uygulanmaz). */
export type ContractorVehicleDeleteBlock = 'openPermits' | 'approvedDocument';
/** Ana firmanın araç silme engelleri (taşeronun "onaylı belge" kilidi ona uygulanmaz). */
export type HostVehicleDeleteBlock = 'openPermits' | 'documentInUse';
export type VehicleDeleteBlock = ContractorVehicleDeleteBlock | HostVehicleDeleteBlock;

type VehicleDeleteFacts = Pick<ContractorVehicleDto, 'hasOpenPermits'> & {
  documents: readonly Pick<VehicleDocumentDto, 'approvalStatus' | 'isInUse'>[];
};

/**
 * Araç silmeyi engelleyen ilk neden (§23.0.7; sunucu `409` ile yine karar verir): önce açık izin;
 * sonra taşeron için ONAYLI belge, ana firma için KULLANIMDAKİ belge. `null` = silinebilir.
 * Dönüş tipi aktöre göre daralır: taşeron hiçbir zaman `documentInUse`, ana firma `approvedDocument` almaz.
 */
export function vehicleDeleteBlock(
  vehicle: VehicleDeleteFacts,
  actor: 'contractor',
): ContractorVehicleDeleteBlock | null;
export function vehicleDeleteBlock(
  vehicle: VehicleDeleteFacts,
  actor: 'host',
): HostVehicleDeleteBlock | null;
export function vehicleDeleteBlock(
  vehicle: VehicleDeleteFacts,
  actor: VehicleActor,
): VehicleDeleteBlock | null {
  if (vehicle.hasOpenPermits) {
    return 'openPermits';
  }
  if (actor === 'contractor') {
    return vehicle.documents.some((d) => d.approvalStatus === 'Approved')
      ? 'approvedDocument'
      : null;
  }
  return vehicle.documents.some((d) => d.isInUse) ? 'documentInUse' : null;
}

/** SP (`web-supplier`) kataloğundaki "Sil" etiket anahtarları. */
export type ContractorVehicleDeleteLabelKey =
  | 'vehicles.action.delete'
  | 'vehicles.action.deleteBlocked'
  | 'vehicles.action.deleteBlockedApproved';
/** EP (`web-expert`) kataloğundaki "Sil" etiket anahtarları. */
export type HostVehicleDeleteLabelKey =
  | 'vehicles.action.delete'
  | 'vehicles.action.deleteBlocked'
  | 'vehicles.action.deleteBlockedInUse';

/**
 * Devre dışı "Sil" öğesinin etiket anahtarı (neden etikette taşınır). `actor` yalnız tip içindir:
 * engel ve anahtar tipleri aktöre göre daralır, böylece bir portal ötekinin anahtarını derleme
 * zamanında alamaz (`deleteBlockedApproved` yalnız SP'de, `deleteBlockedInUse` yalnız EP'de vardır).
 */
export function vehicleDeleteLabelKey(
  block: ContractorVehicleDeleteBlock | null,
  actor: 'contractor',
): ContractorVehicleDeleteLabelKey;
export function vehicleDeleteLabelKey(
  block: HostVehicleDeleteBlock | null,
  actor: 'host',
): HostVehicleDeleteLabelKey;
export function vehicleDeleteLabelKey(
  block: VehicleDeleteBlock | null,
  _actor: VehicleActor,
): ContractorVehicleDeleteLabelKey | HostVehicleDeleteLabelKey {
  switch (block) {
    case 'openPermits':
      return 'vehicles.action.deleteBlocked';
    case 'approvedDocument':
      return 'vehicles.action.deleteBlockedApproved';
    case 'documentInUse':
      return 'vehicles.action.deleteBlockedInUse';
    case null:
      return 'vehicles.action.delete';
  }
}

/** Belge silinebilir mi: taşeron yalnız `Pending`; ana firma `isInUse` değilse (§23.0.4). */
export function canDeleteDocument(
  document: Pick<VehicleDocumentDto, 'approvalStatus' | 'isInUse'>,
  actor: VehicleActor,
): boolean {
  return actor === 'contractor' ? document.approvalStatus === 'Pending' : !document.isInUse;
}

/** Araçlar bölümü/sekmesi gösterilir mi (§14): bayrak VEYA bağlı araç — bağlı araç gizlenmez. */
export function hasVehicleSection(hasVehicles: boolean, vehicles: readonly unknown[]): boolean {
  return hasVehicles || vehicles.length > 0;
}

export type VehicleDocumentSummary =
  | 'none'
  | 'rejected'
  | 'expired'
  | 'missingEnd'
  | 'pending'
  | 'expiring'
  | 'allApproved';

type ProblemState = Exclude<VehicleDocumentSummary, 'none' | 'allApproved'>;

// Her portal kendi kullanıcısının yapması gereken işi öne alır (design §3).
const PRIORITY: Record<VehicleActor, readonly ProblemState[]> = {
  contractor: ['rejected', 'expired', 'missingEnd', 'pending', 'expiring'],
  host: ['pending', 'rejected', 'expired', 'missingEnd', 'expiring'],
};

type SummaryDocument = Pick<VehicleDocumentDto, 'type' | 'approvalStatus' | 'validityStatus'>;

/**
 * Belge hazırlığa sayıyorsa geçerliliği (`Valid`/`Expiring`), saymıyorsa `null`. §23.0.3: ONAYLI bir araç
 * belgesi için `validityStatus ∈ {Valid, Expiring}` ⇔ hazırlığa sayar — istemci tarih okumaz, sunucunun
 * tür başına türettiği `validityStatus`'ü okur.
 */
function readinessValidity(document: SummaryDocument): 'Valid' | 'Expiring' | null {
  if (document.approvalStatus !== 'Approved') {
    return null;
  }
  return document.validityStatus === 'Valid' || document.validityStatus === 'Expiring'
    ? document.validityStatus
    : null;
}

/**
 * Liste satırı için TEK durum (en kötüsü önce; sıra portala göre — `PRIORITY`). Hazırlık DEĞİLDİR — kayıtta
 * izin bağlamı yoktur; "Hazır/Eksik" asla burada üretilmez. `Missing` yalnız tarihsiz Sigorta/Muayene olabilir.
 *
 * **Yenilenmiş belge sayılmaz (§23.0.3).** Bir tür, o türde hazırlığa sayan (onaylı + `Valid`/`Expiring`)
 * en az bir belge varken KARŞILANMIŞTIR; izinler aracı o türde hazır görür. Karşılanmış türde:
 * - `Rejected` ve `Expired`/`Missing` belgeler YOK SAYILIR — yenileme yapılmış, eski belge kullanımda
 *   olduğu için kimse silemez; saymak sütunu sonsuza dek kırmızı bırakırdı.
 * - Türün geçerliliği EN İYİ onaylı belgesinden gelir: biri `Valid` ise diğer onaylı belgenin `Expiring`'i
 *   de yok sayılır (erken yenileme); yalnız `Expiring` varsa "Süresi yaklaşıyor" kalır.
 * - `Pending` belge (ör. erken yüklenen yenileme) DÜŞMEZ — karar bekleyen iştir — ama yalnız onay boyutunu
 *   taşır, kendi geçerliliği sayılmaz. "Onay bekliyor" olarak nerede çıkacağını mevcut öncelik belirler:
 *   taşeronda yalnız daha kötüsü kalmadıysa; ana firmada önce (bekleyen iş onun — design §3).
 * Karşılanmamış türde her belge eskisi gibi sayılır. Kurallar iki portalda da BU işlevden okunur.
 *
 * | Belgeler (aynı aracın)                                    | Taşeron       | Ana firma     |
 * | --------------------------------------------------------- | ------------- | ------------- |
 * | Sigorta Onaylı+Expired, Sigorta Onaylı+Valid              | allApproved   | allApproved   |
 * | Sigorta Reddedildi, Sigorta Onaylı+Valid                  | allApproved   | allApproved   |
 * | Sigorta Onaylı+Expiring, Sigorta Onaylı+Valid             | allApproved   | allApproved   |
 * | Sigorta Onaylı+Expired, Sigorta Onaylı+Expiring           | expiring      | expiring      |
 * | Sigorta Onaylı+Valid, Sigorta Bekliyor+Missing            | pending       | pending       |
 * | Sigorta Onaylı+Expired (tek)                              | expired       | expired       |
 * | Sigorta Onaylı+Expired, Sigorta Bekliyor+Valid            | expired       | pending       |
 * | Ruhsat Onaylı+Valid, Muayene Reddedildi                   | rejected      | rejected      |
 * | Sigorta Onaylı+Valid + Bekliyor, Muayene Onaylı+Expired   | expired       | pending       |
 */
export function summarizeVehicleDocuments(
  documents: readonly SummaryDocument[],
  viewer: VehicleActor,
): VehicleDocumentSummary {
  if (documents.length === 0) {
    return 'none';
  }
  // Karşılanmış türlerin en iyi geçerliliği (`Valid` > `Expiring`).
  const satisfied = new Map<SummaryDocument['type'], 'Valid' | 'Expiring'>();
  for (const document of documents) {
    const validity = readinessValidity(document);
    if (validity !== null && satisfied.get(document.type) !== 'Valid') {
      satisfied.set(document.type, validity);
    }
  }
  const present: Record<ProblemState, boolean> = {
    rejected: false,
    pending: false,
    expired: false,
    missingEnd: false,
    expiring: [...satisfied.values()].includes('Expiring'),
  };
  for (const document of documents) {
    if (document.approvalStatus === 'Pending') {
      present.pending = true;
    }
    if (satisfied.has(document.type)) {
      continue; // karşılanmış tür: eski/reddedilmiş belge ve bekleyenin geçerliliği sayılmaz
    }
    if (document.approvalStatus === 'Rejected') {
      present.rejected = true;
    }
    if (document.validityStatus === 'Expired') {
      present.expired = true;
    } else if (document.validityStatus === 'Missing') {
      present.missingEnd = true;
    } else if (document.validityStatus === 'Expiring') {
      present.expiring = true;
    }
  }
  return PRIORITY[viewer].find((state) => present[state]) ?? 'allApproved';
}

/** İzne araç eklerken ardışık POST'ların kısmi hatası: hangi plaka düştü, hangileri bağlandı. */
export class VehicleLinkBatchError extends Error {
  constructor(
    readonly failedPlate: string,
    readonly linkedPlates: readonly string[],
    readonly original: unknown,
  ) {
    super('VEHICLE_LINK_BATCH_FAILED');
    this.name = 'VehicleLinkBatchError';
  }
}

/**
 * `POST /…/permits/{uuid}/vehicles` tek araçlıdır; seçilenleri SIRAYLA bağlar, ilk hatada durur ve
 * `VehicleLinkBatchError` fırlatır. Bağlar idempotenttir (`200`), yeniden denemek güvenlidir.
 */
export async function linkVehiclesSequentially(
  items: readonly VehicleWriteRequest[],
  link: (body: VehicleWriteRequest) => Promise<unknown>,
): Promise<void> {
  const linked: string[] = [];
  for (const item of items) {
    try {
      await link(item);
      linked.push(item.plate);
    } catch (error) {
      throw new VehicleLinkBatchError(item.plate, linked, error);
    }
  }
}

export type PlateIssue = 'required' | 'tooLong' | 'keyTooLong' | 'invalidChars';

/**
 * Görünen plaka (`displayPlate` çıktısı) için istemci kuralı (§23.0.2) — sunucu yine karar verir.
 * Öncelik: boş → `required`; > 50 → `tooLong`; denetim/biçim karakteri (Cc, Cf) → `invalidChars`; anahtar boş → `required`; anahtar > 20 → `keyTooLong`.
 * `allowEmpty`: boş plaka serbest (izne ekleme çekmecesi: yalnız kayıttan seçim).
 */
export function plateIssue(display: string, allowEmpty = false): PlateIssue | null {
  if (display.length === 0) {
    return allowEmpty ? null : 'required';
  }
  if (display.length > VEHICLE_PLATE_MAX) {
    return 'tooLong';
  }
  if (/[\p{Cc}\p{Cf}]/u.test(display)) {
    return 'invalidChars';
  }
  const key = plateKey(display);
  if (key.length === 0) {
    return 'required';
  }
  return key.length > VEHICLE_PLATE_KEY_MAX ? 'keyTooLong' : null;
}

/** `plateIssue` sonucunun kullanıcı metni (iki portalda ortak `vehicles.validation.*` anahtarları). */
export function plateIssueMessage(issue: PlateIssue, t: TFunction): string {
  switch (issue) {
    case 'required':
      return t('vehicles.validation.plateRequired');
    case 'tooLong':
      return t('vehicles.validation.plateTooLong', { max: VEHICLE_PLATE_MAX });
    case 'keyTooLong':
      return t('vehicles.validation.plateKeyTooLong', { max: VEHICLE_PLATE_KEY_MAX });
    case 'invalidChars':
      return t('vehicles.validation.plateInvalid', {
        max: VEHICLE_PLATE_MAX,
        keyMax: VEHICLE_PLATE_KEY_MAX,
      });
  }
}

/** Açıklama (kırpılmış) üst sınırı aşıyor mu. */
export function descriptionTooLong(text: string): boolean {
  return text.length > VEHICLE_DESCRIPTION_MAX;
}

export function vehicleDescriptionTooLongMessage(t: TFunction): string {
  return t('vehicles.validation.descriptionTooLong', { max: VEHICLE_DESCRIPTION_MAX });
}

export type VehicleFormField = 'plate' | 'description';
export type VehicleFieldError = { field: VehicleFormField; code: 'REQUIRED' | 'INVALID' };

/** `400 VALIDATION_FAILED` `errors[]` içinden forma bağlanabilen kısım (`inviteFieldErrors` deseni). */
export function vehicleFieldErrors(error: unknown): VehicleFieldError[] {
  if (!isApiError(error) || error.errorCode !== 'VALIDATION_FAILED') {
    return [];
  }
  const out: VehicleFieldError[] = [];
  for (const item of error.errors) {
    if (
      (item.field === 'plate' || item.field === 'description') &&
      (item.code === 'REQUIRED' || item.code === 'INVALID')
    ) {
      out.push({ field: item.field, code: item.code });
    }
  }
  return out;
}

/**
 * Sunucunun alan hatasını kullanıcı metnine çevirir (form çekmecesi ve izne araç ekleme ortak).
 * `submittedDescription` = gönderilen (kırpılmış) açıklama: `description` `INVALID` iki nedenle gelir
 * (§23.0.5: 500'den uzun YA DA NUL karakteri) — uzun değilse "en fazla 500" yanlış olurdu; o zaman
 * genel doğrulama metni (`VALIDATION_FAILED`) gösterilir.
 */
export function vehicleFieldErrorMessage(
  item: VehicleFieldError,
  error: unknown,
  t: TFunction,
  submittedDescription: string,
): string {
  if (item.field === 'plate' && item.code === 'REQUIRED') {
    return t('vehicles.validation.plateRequired');
  }
  if (item.field === 'plate' && item.code === 'INVALID') {
    return t('vehicles.validation.plateInvalid', {
      max: VEHICLE_PLATE_MAX,
      keyMax: VEHICLE_PLATE_KEY_MAX,
    });
  }
  if (
    item.field === 'description' &&
    item.code === 'INVALID' &&
    descriptionTooLong(submittedDescription.trim())
  ) {
    return vehicleDescriptionTooLongMessage(t);
  }
  return resolveApiErrorMessage(error, t);
}

/** Kısmi bağlanmadan sonra işaretli kalacak kayıt araçları: bağlananlar (plakayla) işaretten düşer. */
export function remainingSelection(
  selected: ReadonlySet<string>,
  options: readonly { uuid: string; plate: string }[],
  linkedPlates: readonly string[],
): Set<string> {
  const done = new Set(linkedPlates);
  const next = new Set<string>();
  for (const uuid of selected) {
    const v = options.find((o) => o.uuid === uuid);
    if (v && !done.has(v.plate)) {
      next.add(uuid);
    }
  }
  return next;
}

/**
 * Toplu ekleme hatası TAMAMEN forma bağlandıysa banner gösterilmez: hiçbir şey bağlanmadı, düşen
 * öğe elle girilen plaka (`submittedPlate`, hata ANINDAKİ gönderim) ve tüm sunucu hataları alanlara eşlendi.
 */
export function batchErrorFullyMapped(
  error: VehicleLinkBatchError,
  submittedPlate: string | undefined,
): boolean {
  const mapped = vehicleFieldErrors(error.original).length;
  const total = isApiError(error.original) ? error.original.errors.length : 0;
  return (
    error.linkedPlates.length === 0 &&
    mapped > 0 &&
    total <= mapped &&
    error.failedPlate === submittedPlate
  );
}
